import React, { useState, useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface HeightInputProps {
  form: UseFormReturn<any>;
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  className?: string;
}

type UnitType = 'cm' | 'ft-in';

const HeightInput: React.FC<HeightInputProps> = ({
  form,
  name,
  label,
  placeholder = 'Enter height',
  required = false,
  className,
}) => {
  const [feet, setFeet] = useState<string>('');
  const [inches, setInches] = useState<string>('');

  const heightValue = form.watch(name);

  // Initialize values from form
  useEffect(() => {
    if (heightValue !== undefined && heightValue !== null && heightValue !== '') {
      const totalInchesValue = parseFloat(heightValue.toString());
      if (!isNaN(totalInchesValue)) {
        setFeet(Math.floor(totalInchesValue / 12).toString());
        setInches(Math.round(totalInchesValue % 12).toString());
      }
      return;
    }

    setFeet('');
    setInches('');
  }, [heightValue]);



  // Convert ft/in to inches
  const feetInchesToInches = (feet: number, inches: number): number => {
    return feet * 12 + inches;
  };


  const handleFeetInchesChange = (feetValue: string, inchesValue: string) => {
    if (parseFloat(feetValue) >= 0 && parseFloat(feetValue) <= 8) {

      setFeet(feetValue);
    }

    if (parseFloat(inchesValue) >= 0 && parseFloat(inchesValue) <= 11) {

      setInches(inchesValue);
    }

    const ft = parseFloat(feetValue) || 0;
    const inch = parseFloat(inchesValue) || 0;

    if (feetValue === '' && inchesValue === '') {
      form.setValue(name, undefined, { shouldValidate: true });
      return;
    }

    if (ft >= 0 && inch >= 0) {
      const totalInchesValue = feetInchesToInches(ft, inch);
      form.setValue(name, parseFloat(totalInchesValue.toFixed(2)), { shouldValidate: true });
    }
  };


  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <Label className={required ? 'required' : ''}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}

      <div className="flex gap-2">
        <div className="flex-1">
          <div className="flex gap-2">
            <div className="flex items-center gap-2 flex-1">
              <Input
                type="number"
                step="1"
                min="0"
                max="10"
                value={feet}
                onChange={(e) => handleFeetInchesChange(e.target.value, inches)}
                placeholder="Feet"
                className="flex-1"
              />
              <span className="text-sm text-slate-500 font-bold">ft</span>
              <Input
                type="number"
                step="1"
                min="0"
                max="11"
                value={inches}
                onChange={(e) => handleFeetInchesChange(feet, e.target.value)}
                placeholder="In"
                className="flex-1"
              />
              <span className="text-sm text-slate-500 font-bold">in</span>
            </div>
            <Button
              type="button"
              variant="outline"
              className="whitespace-nowrap"
            >
              ft/in
            </Button>
          </div>
        </div>
      </div>

      {form.formState.errors[name] && (
        <p className="text-[0.8rem] text-red-500">
          {form.formState.errors[name]?.message as string}
        </p>
      )}
    </div>
  );
};

export default HeightInput;

